/**
 * Convert an array of highlights into RTE annotations.
 * @param {Object} highlighter - an object containing a highlights property.
 * @param {Array} highligher.highlights[] - an array of highlight objects.
 * @param {number} highlighter.highlights[].highlight[0] - the position where the highlight starts.
 * @param {number} highlighter.highlights[].highlight[1] - the position where the highlight ends (exclusive).
 * @param {string} highlighter.highlights[].className - the class name used to indicate the highlighter color.
 * @param {array} highlighter.DraftText[] - legacy top-level highlighter object property, replaced by highlights.
 * Example input:
 *     {
 *       "highlights": [{
 *         "highlight": [7,16],
 *         "className": "color-green"
 *       }]
 *     }
 * @returns {Object[]} - an array of highlighter annotation objects.
 * Example output:
 *     [{
 *       from: 7,
 *       length: 9,
 *       payload: {
 *         type: "highlighter",
 *         color: "green"
 *       }
 *     }]
 */

import { ContentFormat } from "../features/writing/drafting/draftingApiSlice";
import { ExternalAnnotation, HighlighterPayload } from "../rte/Annotations";
import RichTextWrapper, { Props as RteProps, stfToHtmlImpl } from "../rte/ExternalApi";
import { ScorableTextFormat } from "../rte/ScorableTextFormat";
import { countWords } from "./utils";

export interface Highlight {
	highlight: number[];
	className: string;
}

export interface Highlighter {
	highlights: Highlight[];
	// DraftText: string[],  // don't support DraftText for now
}

export function convertHighlightsToRteAnnotations(highlighter: Highlighter): ExternalAnnotation[] {
	if (!highlighter || !highlighter.highlights) {
		return [];
	}

	return highlighter.highlights.map(
		(h) =>
			<ExternalAnnotation>{
				from: h.highlight[0],
				length: h.highlight[1] - h.highlight[0],
				payload: {
					type: "highlighter",
					color: h.className.slice(6),
				},
			}
	);
}

/**
 * Convert an array of draft comments into RTE annotations.
 * @param {any[]} comments - an array of comment objects.
 * @param {number} comments.id - the ID of the comment.
 * @param {number} comments.start - the position where the comment starts.
 * @param {number} comments.end - the position where the comment ends (inclusive).
 * @param {string} comments.type - the type of comment (spelling, grammar, comment, plagiarism).
 * @param {string} comments.text - the text of the comment.
 * @param {boolean} comments.editable - indicates whether the comment can be edited.
 * Example input:
 *     [{
 *       "id": 651,
 *       "start": 309,
 *       "end": 318,
 *       "type": "spelling",
 *       "text": "This word may be misspelled. Did you mean \"chocolate\"?",
 *       "editable": true
 *     }]
 * @returns {Object[]} - an array of comment annotation objects.
 * Example output:
 *     [{
 *       from: 309,
 *       length: 10,
 *       payload: {
 *         id: 651
 *         type: "comment",
 *         text: "This word may be misspelled. Did you mean \"chocolate\"?",
 *         editable: True,
 *       }
 *     }]
 */

export interface Comment {
	id: number;
	start: number;
	end: number;
	type: "spelling" | "grammar" | "comment" | "plagiarism";
	text: string;
	editable: boolean;
}
export function convertCommentsToRteAnnotations(comments: Comment[]): ExternalAnnotation[] {
	if (!comments) {
		return [];
	}

	return comments.map((c) => ({
		from: c.start,
		length: c.end - c.start + 1, // Alert! end is inclusive
		payload: {
			id: c.id,
			type: "comment",
			commentType: c.type,
			text: c.text || "",
			editable: c.editable,
		},
	}));
}

/**
 * Convert RTE highlighter annotations into an array of highlights.
 * @param {string} annotations - an array of annotations which may include highlighter annotations.
 * @returns {Object[]} - an array of highlight objects.
 */

// export interface RteAnnotation {
// 	payload: {
// 		type: string;
// 		color: string;
// 	};
// 	from: number;
// 	length: number;
// }
export const convertRteAnnotationsToHighlights = (
	annotations: ExternalAnnotation[]
): Highlighter => {
	if (!annotations) {
		return {
			highlights: [],
		};
	}

	return {
		highlights: annotations
			.filter((a) => a.payload.type === "highlighter")
			.map((a) => ({
				highlight: [a.from, a.from + a.length],
				className: "color-" + (a.payload as HighlighterPayload).color,
			})),
	};
};

// /**
//  * Count the number of words in a RichTextEditor control. The word count logic
//  * yields the same results as the "countable" package.
//  * @param {Object} $rte - a RichTextEditor control.
//  * @returns {Number} - the number of words in the RTE.
//  */
// const countWords = ($rte: RichTextWrapper) => {
//     if (!$rte) {
//         return undefined;
//     }
//     const stf = $rte.getStf();
//     if (!stf) {
//         return undefined;
//     }

//     const wordCount = (stf.text.replace(/['";:,.?¿\-!¡]+/g, '').match(/\S+/g) || []).length;
//     return wordCount;
// }

export const countRteWords = (rte: RichTextWrapper) => {
	const doc = rte.state.editor.doc;
	return countWords(doc.textBetween(0, doc.nodeSize - 2, "\n"));
};

export interface RteContent {
	format: ContentFormat;
	content: string;
	markup: string;
}

export const setRteProps = (
	props: RteProps,
	content: RteContent,
	highlights: Highlighter,
	comments: Comment[]
) => {
	props.initialHtml = undefined;
	props.initialStf = undefined;
	props.initialText = undefined;

	switch (content.format.toUpperCase()) {
		case "HTML":
			// Highlights and comments not available in HTML mode.
			props.initialHtml = content.content || "";
			break;
		case "STF":
			props.initialStf = {
				text: content.content || "",
				markup: content.markup,
				annotations: [
					...convertHighlightsToRteAnnotations(highlights),
					...convertCommentsToRteAnnotations(comments),
				],
			};
			break;
		case "TEXT":
			props.initialText = {
				text: content.content || "",
				annotations: [
					...convertHighlightsToRteAnnotations(highlights),
					...convertCommentsToRteAnnotations(comments),
				],
			};
			break;
	}
};

export function textToHtml(text: string) {
	const paras = text.split(/\r?\n/);
	const html = paras.reduce((a, v) => a + `<p>${v}</p>`, "");
	return html;
}

export function getHtml(
	format: ContentFormat,
	content: string,
	markup: string,
	annotations: ExternalAnnotation[] = []
) {
	switch (format) {
		case ContentFormat.Text:
			return textToHtml(content);
		case ContentFormat.Html:
			return content;
		case ContentFormat.Stf:
			if (content && markup) {
				const responseStf: ScorableTextFormat = {
					text: content,
					markup: markup,
					annotations: annotations,
				};
				return stfToHtmlImpl(responseStf);
			}
	}
	return "";
}
